import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react"; // Import Lucide icons
import { 
  StickyNav, 
  Nav, 
  Logo, 
  LogoImage,
  LogoText,
  NavLinks,
  HamburgerButton,
  HamburgerLine,
  Overlay,
  BookSpaceButton,
  NavContainer,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownArrow
} from "./NavigationStyles";

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Always show scrolled view on contact page
    if (location.pathname === "/contact-us") {
      setIsScrolled(true);
      return;
    }
    
    // Regular scroll detection for other pages
    const handleScroll = () => {
      const heroSection = document.getElementById('hero-section');
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
        setIsScrolled(window.scrollY > heroBottom - 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (menuOpen) {
      setOpenDropdown(null);
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleBookSpace = () => {
    navigate("/book-a-space");
    closeMenu();
  };

  const toggleDropdown = (dropdownName) => {
    if (openDropdown === dropdownName) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(dropdownName);
    }
  };

  const handleDropdownLinkClick = (path) => {
    navigate(path);
    closeMenu();
    setOpenDropdown(null);
  };

  return (
    <>
      <StickyNav 
        className={`${isScrolled ? 'scrolled' : ''} ${location.pathname === '/contact-us' ? 'contact-page' : ''}`}
      >
        <Nav>
          <Logo>
            <Link to="/">
              <LogoImage 
                src="/AugustTwentyLogo.png" 
                alt="August Twenty Logo" 
              />
            </Link>
          </Logo>
          
          <HamburgerButton 
            className={menuOpen ? 'open' : ''} 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <HamburgerLine />
            <HamburgerLine />
            <HamburgerLine />
          </HamburgerButton>

          <NavContainer>
            <NavLinks className={menuOpen ? 'open' : ''}>
              {/* Consultancy Services Dropdown */}
              <Dropdown ref={dropdownRef}>
                <button 
                  className={`dropdown-toggle ${openDropdown === 'consultancy' ? 'active' : ''}`}
                  onClick={() => toggleDropdown('consultancy')}
                >
                  Consultancy Services
                  <DropdownArrow className={openDropdown === 'consultancy' ? 'open' : ''}>
                    {openDropdown === 'consultancy' ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </DropdownArrow>
                </button>
                <DropdownContent className={openDropdown === 'consultancy' ? 'open' : ''}>
                  <DropdownItem onClick={() => handleDropdownLinkClick("/agribusiness")}>
                    Agribusiness
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDropdownLinkClick("/erp")}>
                    Business Applications
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDropdownLinkClick("/immigration")}>
                    Corporate Immigration
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDropdownLinkClick("/project-management")}>
                    Project Management
                  </DropdownItem>
                  <DropdownItem onClick={() => handleDropdownLinkClick("/property-management")}>
                    Property Management
                  </DropdownItem>
                </DropdownContent>
              </Dropdown>

              {/* Regular Links */}
              <Link to="/horton-house" onClick={closeMenu}>Workspace & Collaboration</Link>
              <Link to="/our-team" onClick={closeMenu}>Our Team</Link>
              <Link to="/contact-us" onClick={closeMenu}>Contact Us</Link>
              
              {/* Book A Space Button in mobile menu */}
              <BookSpaceButton 
                className="mobile-only" 
                onClick={handleBookSpace}
              >
                Book A Space
              </BookSpaceButton>
            </NavLinks>

            {/* Book A Space Button for desktop */}
            <BookSpaceButton 
              className="desktop-only"
              onClick={handleBookSpace}
            >
              Book A Space
            </BookSpaceButton>
          </NavContainer>
        </Nav>
      </StickyNav>

      <Overlay className={menuOpen ? 'open' : ''} onClick={closeMenu} />
    </>
  );
}